import { $, $$, OverlayElement, announce, fetchHTML, formatMoney, prefersReducedMotion, publish, subscribe, theme, toast } from './utils.js';
import { cartAdd } from './cart.js';

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

/* ---------- gallery: one swipeable image + thumbnails (click = jump), colour filtering, zoom ---------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.wrap = this.closest('.gal-wrap');
    this.counter = $('[data-gallery-counter]', this.wrap);
    this.thumbs = $$('[data-thumb]', this.wrap);
    this.prev = $('[data-gal-prev]', this.wrap);
    this.next = $('[data-gal-next]', this.wrap);
    this.addEventListener('click', (event) => {
      const zoom = event.target.closest('[data-zoom]');
      if (!zoom) return;
      const box = document.getElementById(`lightbox-${this.dataset.sectionId}`);
      if (box && typeof box.show === 'function') box.show(this, Number(zoom.dataset.index), zoom);
    });
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goToMedia(thumb.dataset.mediaId)));
    if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
    if (this.next) this.next.addEventListener('click', () => this.step(1));
    this.addEventListener(
      'scroll',
      () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.sync();
        });
      },
      { passive: true }
    );
    this.unsubscribe = subscribe('variant:change', (event) => this.onVariant(event.detail));
    this.sync();
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  get items() {
    return $$('.gal-item:not([hidden])', this);
  }

  get index() {
    let best = 0;
    let distance = Infinity;
    this.items.forEach((item, i) => {
      const d = Math.abs(item.offsetLeft - this.scrollLeft);
      if (d < distance) {
        distance = d;
        best = i;
      }
    });
    return best;
  }

  goTo(item) {
    if (!item) return;
    this.scrollTo({ left: item.offsetLeft, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  goToMedia(mediaId) {
    this.goTo($(`.gal-item[data-media-id="${CSS.escape(String(mediaId))}"]`, this));
  }

  step(delta) {
    const items = this.items;
    this.goTo(items[Math.max(0, Math.min(items.length - 1, this.index + delta))]);
  }

  /* keeps counter, arrows and the active thumbnail in step with the image on screen */
  sync() {
    const items = this.items;
    if (!items.length) return;
    const index = this.index;
    const active = items[index];
    if (this.counter) this.counter.textContent = `${index + 1} / ${items.length}`;
    if (this.prev) this.prev.disabled = index === 0;
    if (this.next) this.next.disabled = index === items.length - 1;
    let current = null;
    this.thumbs.forEach((thumb) => {
      const isActive = thumb.dataset.mediaId === active.dataset.mediaId;
      thumb.setAttribute('aria-current', String(isActive));
      if (isActive) current = thumb;
    });
    if (current) this.centerThumb(current);
  }

  centerThumb(thumb) {
    const strip = thumb.closest('ul');
    if (!strip) return;
    const s = strip.getBoundingClientRect();
    const t = thumb.getBoundingClientRect();
    const left = strip.scrollWidth > strip.clientWidth + 1 ? t.left - s.left - (s.width - t.width) / 2 : 0;
    const top = strip.scrollHeight > strip.clientHeight + 1 ? t.top - s.top - (s.height - t.height) / 2 : 0;
    if (left || top) strip.scrollBy({ left, top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  onVariant({ sectionId, color, mediaId, tone }) {
    if (sectionId !== this.dataset.sectionId) return;
    const items = $$('.gal-item', this);
    const tagged = items.some((item) => item.dataset.color);
    if (color && tagged) {
      items.forEach((item) => {
        const itemColor = item.dataset.color;
        item.hidden = Boolean(itemColor) && itemColor.toLowerCase() !== color.toLowerCase();
      });
      this.thumbs.forEach((thumb) => {
        const itemColor = thumb.dataset.color;
        thumb.closest('li').hidden = Boolean(itemColor) && itemColor.toLowerCase() !== color.toLowerCase();
      });
      this.scrollLeft = 0;
    } else if (mediaId) {
      this.goToMedia(mediaId);
    }
    const hero = $('[data-placeholder-hero]', this);
    if (hero && tone) hero.style.background = tone;
    this.sync();
  }
}
define('product-gallery', ProductGallery);

class ProductLightbox extends OverlayElement {
  connectedCallback() {
    super.connectedCallback();
    if (this._lb) return;
    this._lb = true;
    this.image = $('[data-lightbox-image]', this);
    $('[data-prev]', this).addEventListener('click', () => this.step(-1));
    $('[data-next]', this).addEventListener('click', () => this.step(1));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    });
  }
  show(gallery, index, opener) {
    this.sources = $('[data-zoom]', gallery)
      .filter((el) => !el.closest('[hidden]'))
      .map((el) => ({ slot: Number(el.dataset.index), src: el.dataset.full, alt: el.closest('.gal-item').querySelector('img')?.alt || '' }));
    this.index = Math.max(0, this.sources.findIndex((source) => source.slot === index));
    this.paint();
    this.open(opener);
  }
  step(delta) {
    this.index = (this.index + delta + this.sources.length) % this.sources.length;
    this.paint();
  }
  paint() {
    const item = this.sources[this.index];
    this.image.src = item.src;
    this.image.alt = item.alt;
  }
}
define('product-lightbox', ProductLightbox);

/* ---------- variant picker ---------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const json = $('[data-product-json]', this);
    if (!json) return;
    this.product = JSON.parse(json.textContent);
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;
    this.sizePos = Number(this.dataset.sizeIndex);
    this.colorPos = Number(this.dataset.colorIndex);
    this.root = this.closest('[data-product-section]');
    this.groups = $$('[data-option-group]', this);
    this.options = this.product.options.map(() => '');
    this.groups.forEach((group) => {
      const pressed = $('[aria-pressed="true"]', group);
      this.options[Number(group.dataset.position)] = pressed ? pressed.dataset.value : '';
    });
    // products with only the default variant have no groups
    if (this.groups.length === 0 && this.product.variants.length === 1) this.options = this.product.variants[0].options.slice();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-value]');
      if (!button || button.disabled) return;
      const group = button.closest('[data-option-group]');
      this.select(Number(group.dataset.position), button.dataset.value);
    });
    this.refreshAvailability();
    this.variant = this.findVariant();
    this.sizeGroup = $('[data-option-group][data-kind="size"]', this);
  }

  complete() {
    return this.options.every((value) => value !== '');
  }

  findVariant() {
    if (!this.complete()) return null;
    return this.product.variants.find((variant) => variant.options.every((value, i) => value === this.options[i])) || null;
  }

  select(position, value) {
    this.options[position] = value;
    const group = this.groups.find((g) => Number(g.dataset.position) === position);
    $$('button[data-value]', group).forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.value === value)));
    const label = $('[data-selected-label]', group);
    if (label) label.textContent = group.dataset.kind === 'color' ? value : `· ${value}`;

    this.refreshAvailability();
    this.variant = this.findVariant();
    this.hideSizeError();
    this.apply();
  }

  /* Disable option values with no purchasable variant for the current selection */
  refreshAvailability() {
    this.groups.forEach((group) => {
      const position = Number(group.dataset.position);
      $$('button[data-value]', group).forEach((button) => {
        const candidate = button.dataset.value;
        const available = this.product.variants.some((variant) => {
          if (variant.options[position] !== candidate || !variant.available) return false;
          if (group.dataset.kind === 'color') return true;
          return variant.options.every((value, i) => i === position || !this.options[i] || value === this.options[i] || i === this.sizePos);
        });
        button.disabled = !available;
        if (!available && button.getAttribute('aria-pressed') === 'true' && group.dataset.kind !== 'color') {
          button.setAttribute('aria-pressed', 'false');
          this.options[position] = '';
          const label = $('[data-selected-label]', group);
          if (label) label.textContent = '';
        }
      });
    });
  }

  apply() {
    const variant = this.variant;
    const color = this.colorPos >= 0 ? this.options[this.colorPos] : '';
    const colorVariant = variant || this.product.variants.find((v) => color && v.options[this.colorPos] === color);
    const colorButton = color && $(`[data-kind="color"] [data-value="${CSS.escape(color)}"]`, this);

    publish('variant:change', {
      sectionId: this.sectionId,
      variant,
      color,
      mediaId: colorVariant && colorVariant.featured_media ? colorVariant.featured_media.id : null,
      tone: colorButton ? colorButton.dataset.tone : null,
    });

    const idInput = $('[data-variant-id]', this.root);
    if (idInput) idInput.value = variant ? variant.id : '';

    const addButton = $('[data-add-to-cart]', this.root);
    const addLabel = $('[data-add-label]', this.root);
    if (variant) {
      if (addButton) addButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? theme.strings.addToBag : theme.strings.soldOut;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      this.refreshSections(variant);
    } else if (addButton && addLabel) {
      addButton.disabled = false;
      addLabel.textContent = theme.strings.addToBag;
    }
    const stickyLabel = $('[data-sticky-variant]');
    if (stickyLabel) stickyLabel.textContent = this.complete() ? this.options.join(' / ') : theme.strings.selectSizeLabel;
  }

  /* Section Rendering API: refresh price / SKU for the chosen variant */
  async refreshSections(variant) {
    const token = (this.token = (this.token || 0) + 1);
    try {
      const doc = await fetchHTML(`${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`);
      if (token !== this.token) return;
      ['[data-product-price-wrap]'].forEach((selector) => {
        const next = $(selector, doc);
        const current = $(selector, this.root);
        if (next && current) current.innerHTML = next.innerHTML;
      });
      const sticky = $('[data-sticky-price]');
      const price = $('[data-price] .now', doc);
      if (sticky && price) sticky.textContent = price.textContent;
      const saleTag = $('[data-sale-tag]', this.root);
      if (saleTag) saleTag.hidden = !$('[data-price] s', doc);
    } catch (error) {
      const sticky = $('[data-sticky-price]');
      if (sticky) sticky.textContent = formatMoney(variant.price);
    }
  }

  showSizeError() {
    const group = this.sizeGroup;
    if (!group) return;
    const error = $('[data-size-error]', group);
    if (error) error.hidden = false;
    const sizes = $('[data-size-group]', group);
    if (sizes) {
      sizes.setAttribute('aria-describedby', error.id);
      sizes.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      sizes.focus({ preventScroll: true });
    }
  }

  hideSizeError() {
    const error = $('[data-size-error]', this);
    if (error) error.hidden = true;
  }
}
define('variant-picker', VariantPicker);

/* ---------- add to bag / buy now ---------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = $('form', this);
    this.root = this.closest('[data-product-section]') || document;
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.add();
    });
    const buyNow = $('[data-buy-now]', this);
    if (buyNow) buyNow.addEventListener('click', () => this.add({ buyNow: true }));
    const dynamic = $('[data-buy-now-wrap]', this);
    if (dynamic) {
      dynamic.addEventListener(
        'click',
        (event) => {
          if (!this.valid()) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        true
      );
    }
    this.unsubscribeSticky = (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) this.add();
    };
    document.addEventListener('sticky:add', this.unsubscribeSticky);
  }

  disconnectedCallback() {
    document.removeEventListener('sticky:add', this.unsubscribeSticky);
  }

  get picker() {
    return $('variant-picker', this.root);
  }

  valid() {
    const picker = this.picker;
    if (picker && !picker.complete()) {
      picker.showSizeError();
      announce(theme.strings.selectSize);
      return false;
    }
    return true;
  }

  async add({ buyNow = false } = {}) {
    if (!this.valid()) return;
    const button = $('[data-add-to-cart]', this.form);
    const label = $('[data-add-label]', this.form);
    const idInput = $('[data-variant-id]', this.form);
    if (!idInput || !idInput.value) return;
    const properties = {};
    new FormData(this.form).forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    });
    button.classList.add('is-loading');
    button.disabled = true;
    const status = $('[data-form-status]', this.form);
    try {
      await cartAdd([{ id: Number(idInput.value), quantity: 1, ...(Object.keys(properties).length ? { properties } : {}) }]);
      const title = $('h1', this.root)?.textContent.trim() || '';
      const picker = this.picker;
      const size = picker && picker.sizePos >= 0 ? picker.options[picker.sizePos] : '';
      const message = size ? theme.strings.addedTo.replace('__TITLE__', title).replace('__SIZE__', size) : `${theme.strings.added}: ${title}`;
      if (status) status.textContent = message;
      announce(message);
      if (label) label.textContent = theme.strings.addedLabel;
      setTimeout(() => {
        if (label) label.textContent = theme.strings.addToBag;
      }, 2200);
      if (buyNow) {
        this.goToCheckout();
        return;
      }
      const drawer = $('cart-drawer');
      if (theme.settings.cartType === 'drawer' && theme.settings.autoOpenCart && drawer) drawer.open(button);
      else toast(message);
    } catch (error) {
      if (status) status.textContent = error.message;
      toast(error.message || theme.strings.error);
      if (label) label.textContent = theme.strings.addToBag;
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  }

  goToCheckout() {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = theme.routes.cart_url;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'checkout';
    input.value = '';
    form.append(input);
    document.body.append(form);
    form.submit();
  }
}
define('product-form', ProductForm);

/* ---------- size guide: 2 tabs with arrow-key navigation + in/cm toggle ---------- */
function initTabs(root) {
  const tablist = $('[data-tablist]', root);
  if (!tablist) return;
  const tabs = $$('[role="tab"]', tablist);
  const select = (tab, focus) => {
    tabs.forEach((other) => {
      const active = other === tab;
      other.setAttribute('aria-selected', String(active));
      other.tabIndex = active ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls'));
      if (panel) panel.hidden = !active;
    });
    if (focus) tab.focus();
  };
  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => select(tab));
    tab.addEventListener('keydown', (event) => {
      const keys = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 };
      if (!(event.key in keys)) return;
      event.preventDefault();
      select(tabs[(keys[event.key] + tabs.length) % tabs.length], true);
    });
  });
  $$('[data-unit-button]', root).forEach((button) =>
    button.addEventListener('click', () => {
      $$('[data-unit-button]', root).forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
      $$('[data-unit]', root).forEach((panel) => (panel.hidden = panel.dataset.unit !== button.dataset.unitButton));
    })
  );
}

class SizeGuide extends OverlayElement {
  connectedCallback() {
    super.connectedCallback();
    if (!this._tabs) {
      this._tabs = true;
      initTabs(this);
    }
  }
}
define('size-guide', SizeGuide);

class SizeGuideTabs extends HTMLElement {
  connectedCallback() {
    initTabs(this);
  }
}
define('size-guide-tabs', SizeGuideTabs);

/* ---------- sticky add-to-bag bar (mobile) ---------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.bar = $('.sticky-atc', this);
    this.button = $('[data-sticky-add]', this);
    const root = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
    const target = root && $('[data-main-atc]', root);
    if (!target || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.bar.classList.toggle('is-visible', show);
      this.bar.setAttribute('aria-hidden', String(!show));
      this.button.tabIndex = show ? 0 : -1;
    });
    this.observer.observe(target);
    this.button.addEventListener('click', () => document.dispatchEvent(new CustomEvent('sticky:add', { detail: { sectionId: this.dataset.sectionId } })));
  }
  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}
define('sticky-atc', StickyAtc);

/* ---------- recommendations (Product Recommendations API) ---------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const load = async () => {
      try {
        const doc = await fetchHTML(this.dataset.url);
        const grid = $('.recs-grid', doc);
        const current = $('.recs-grid', this);
        if (grid && current && $('.card', grid)) current.innerHTML = grid.innerHTML;
      } catch (error) {
        /* keep the server-rendered fallback */
      }
    };
    if (!('IntersectionObserver' in window)) {
      load();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          load();
        }
      },
      { rootMargin: '500px' }
    );
    this.observer.observe(this);
  }
  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}
define('product-recommendations', ProductRecommendations);
